import { Injectable, inject } from '@angular/core'

import { Store } from '@ngrx/store'
import { produce } from 'immer'
import { omit as _omit, uniqBy as _uniqBy } from 'lodash/fp'
import { v4 as uuidv4 } from 'uuid'

import { Observable, catchError, forkJoin, map, of } from 'rxjs'

import {
  ActionAgent,
  ActionTarget,
  AddThreadMessageRequest,
  ContextData,
  ContextItem,
  ConversationDocumentReferenceInput,
  ConversationMessageHint,
  ConversationThread,
  ConversationThreadContextDataItemType,
  ConversationThreadExecutionPlanActionType,
  CreateConversationRequest,
  Message,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import { ChatMessage } from '@flowx/designer/modules/ai/conversation/ai-conversation.dictionary'
import {
  ContextDataKeys,
  MessageType,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import { mapTemplateConfigToAiContext } from '@flowx/designer/modules/ai/conversation/conversation.utils'
import {
  NavigationAreaNormalizedTemplateConfigs,
  NodeApiService,
} from '@flowx/designer/shared/api/node.api'
import { ProjectDataModelApiService } from '@flowx/designer/shared/api/project-data-model.api'
import { BpmnEditorStores } from '@flowx/designer/shared/effector/bpmn-editor/bpmn-editor.stores'
import { ProcessEffector } from '@flowx/designer/shared/effector/process.effector'
import { ManifestInfo } from '@flowx/designer/shared/entities/application.entity'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'
import { selectedAction } from '@flowx/designer/shared/store/selectors/node-actions.selectors'
import { selectNodes } from '@flowx/designer/shared/store/selectors/node.selectors'
import {
  selectAppId,
  selectProcessResourceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'
import {
  selectReusableUiTemplateResourceId,
  selectUiFlowResourceId,
} from '@flowx/designer/shared/store/selectors/template-config.selectors'

import {
  ProjectDataModelCatalogueEntry,
  mapProjectDataModelToCatalogue,
} from './project-data-model-catalogue'

import { AiAnalystUtils } from '../agents/analyst/services/analyst.service'

@Injectable({
  providedIn: 'root',
})
export class AiConversationContextBuilderService {
  #store = inject(Store)
  #urlParamsStore = inject(UrlParametersStoreService)
  #nodeApiService = inject(NodeApiService)
  #projectDataModelApiService = inject(ProjectDataModelApiService)

  buildIntentUnderstandingMessageContext(): Observable<ContextData> {
    // A process may remain cached after navigating to a UI flow. Its templates are
    // forbidden in this route, and its process/node/action context does not belong
    // to the flow. Keep merges these items with the existing UI flow message context.
    if (this.#store.selectSignal(selectUiFlowResourceId)()) {
      return this.buildProjectDataModelContextItem().pipe(
        map((item) => ({ items: item ? [item] : [] }))
      )
    }

    const processDefinition = ProcessEffector.processDefinition$.getState()
    if (!processDefinition) {
      return of({
        items: [],
      })
    }

    const allNodes = this.#store.selectSignal(selectNodes)()
    // The user-task template configs live under the process resource (rt/process/...).
    // In the reusable-ui-template section that resource is forbidden (403), and stale
    // process nodes may still be loaded, so skip the process call and rely on the
    // reusable template context instead.
    const isReusableTemplateTarget = !!this.#store.selectSignal(
      selectReusableUiTemplateResourceId
    )()

    return forkJoin({
      navigationAreaNormalizedTemplateConfigs:
        isReusableTemplateTarget || !allNodes?.length
          ? of(null)
          : this.#nodeApiService.getUserTaskTemplateConfigs(
              allNodes.map((node) => node.navigationAreaId)
            ),
      projectDataModelCatalogue: this.#buildProjectDataModelCatalogue(),
    }).pipe(
      map(({ navigationAreaNormalizedTemplateConfigs, projectDataModelCatalogue }) => {
        const selectedNodeId = BpmnEditorStores.selectedNodeId$.getState()
        const selectedActionId = this.#store.selectSignal(selectedAction(selectedNodeId))()?.id
        const processContext = AiAnalystUtils.getInitialProcessContext()

        const finalContextItems: ContextItem[] = [
          ...this.#buildIntentUnderstandingMessageBaseContext(
            navigationAreaNormalizedTemplateConfigs
          ).items,
          {
            key: ContextDataKeys.AiProcessContext,
            itemType: ConversationThreadContextDataItemType.GenericString,
            valueGenericString: JSON.stringify(processContext),
          },
        ]

        const projectDataModelItem = this.#mapCatalogueToContextItem(projectDataModelCatalogue)
        if (projectDataModelItem) {
          finalContextItems.push(projectDataModelItem)
        }

        if (selectedNodeId) {
          finalContextItems.push({
            key: ContextDataKeys.ReferencedNode,
            itemType: ConversationThreadContextDataItemType.GenericString,
            valueGenericString: JSON.stringify(selectedNodeId.toString()),
          })
        }

        if (selectedActionId) {
          finalContextItems.push({
            key: ContextDataKeys.ReferencedAction,
            itemType: ConversationThreadContextDataItemType.GenericString,
            valueGenericString: JSON.stringify(selectedActionId.toString()),
          })
        }

        // Note: File context is now added to the hint actions, not to message context
        // This is handled in #buildIntentUnderstandingHint method

        return {
          items: finalContextItems,
        }
      })
    )
  }

  getActiveConversationAndThread(): { conversationId: string; threadId: string } {
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const threadId = this.#store.selectSignal(ConversationSelectors.activeAgentThreadId)()

    if (!conversationId || !threadId) {
      throw new Error('Missing conversation or thread ID')
    }

    return { conversationId, threadId }
  }

  buildThreadMessageInput(
    message: ChatMessage & { file?: { id: string; type: string; name: string } },
    threadData: {
      activeThread: ConversationThread
      latestMessage: Message | null
      firstMessage: Message
    },
    documents?: ConversationDocumentReferenceInput[]
  ): AddThreadMessageRequest {
    const { latestMessage, firstMessage } = threadData
    const agent = this.extractAgentFromMessage(firstMessage)

    const messageInput = this.#isFollowUpAfterInitMessage(threadData)
      ? this.#buildFirstUserPromptGraphMessageInput(message, latestMessage, documents)
      : this.#buildStandardMessageInput(message, agent, latestMessage, documents)

    return produce(messageInput, (draft) => {
      draft.contextData.items.push({
        key: ContextDataKeys.UIMessageType,
        itemType: ConversationThreadContextDataItemType.GenericString,
        valueGenericString: message.type,
      })

      // Architect: edit what the user currently sees. Replace the carried-forward (thread-open)
      // data model with the live preview so a follow-up extends/trims the previewed model.
      if (agent.agent === AgentName.AiArchitect) {
        const previewContext = this.#store.selectSignal(
          ConversationSelectors.architectPreviewDataModelContext
        )()
        if (previewContext) {
          draft.contextData.items = [
            ...draft.contextData.items.filter(
              (item) => item.key !== ContextDataKeys.DataModelContext
            ),
            {
              key: ContextDataKeys.DataModelContext,
              itemType: ConversationThreadContextDataItemType.GenericString,
              valueGenericString: JSON.stringify(previewContext),
            },
          ]
        }
      }
    })
  }

  extractAgentFromMessage(firstMessage: Message): ActionAgent {
    const firstAction = firstMessage.executionPlan.actions[0]
    if (firstAction.agent.agent === AgentName.ConnectedGraph) {
      const uiAgentContext = firstMessage.contextData.items.find(
        (item: ContextItem) => item.key === ContextDataKeys.UiAgent
      )

      if (!uiAgentContext?.valueGenericString) {
        throw new Error('UI_AGENT context not found for noop_agent agent')
      }

      return JSON.parse(uiAgentContext.valueGenericString) as ActionAgent
    }

    return firstAction.agent
  }

  buildConversationInputWithoutMessage(): CreateConversationRequest {
    return {
      applicationId: this.#store.selectSignal(selectAppId)(),
      processResourceId: this.#store.selectSignal(selectProcessResourceId)(),
      documents: [],
      contextData: {
        items: [],
      },
    }
  }

  buildFirstMessage(payload: {
    message?: string
    context: ContextData
    agent: ActionAgent | null
    target: ActionTarget
    userPromptRequired: boolean
    file?: { id: string; type: string; name: string }
    hintContextItems?: ContextItem[]
  }): CreateConversationRequest {
    const actionAgent = payload.userPromptRequired
      ? {
          action: AgentAction.NoopStore,
          agent: AgentName.ConnectedGraph,
        }
      : payload.agent

    // Determine if we need to use file-based action
    let finalAgent = actionAgent
    if (payload.file && payload.agent && !payload.userPromptRequired) {
      if (payload.agent.agent === AgentName.AiAnalyst) {
        // Check if file is BPMN
        const isBpmnFile =
          payload.file.type === 'application/bpmn+xml' || payload.file.name.endsWith('.bpmn')
        finalAgent = {
          ...payload.agent,
          action: isBpmnFile
            ? AgentAction.CreateProcessFromBpmn
            : AgentAction.CreateProcessFromImage,
        }
      } else if (payload.agent.agent === AgentName.AiDesigner) {
        finalAgent = {
          ...payload.agent,
          action: AgentAction.CreateUiFromImage,
        }
      }
    }

    // Build action context items
    const actionContextItems: ContextItem[] = payload.hintContextItems
      ? payload.hintContextItems
      : []
    if (payload.file) {
      const contextKey =
        finalAgent.agent === AgentName.AiAnalyst
          ? ContextDataKeys.ProcessReferenceImage
          : finalAgent.agent === AgentName.AiDesigner
            ? ContextDataKeys.DesignerReferenceImage
            : ContextDataKeys.DesignerReferenceImage // default

      actionContextItems.push({
        key: contextKey,
        itemType: ConversationThreadContextDataItemType.BinaryObject,
        valueBinaryObject: {
          tempFileId: payload.file.id,
          fileName: payload.file.name,
          mimeType: payload.file.type,
        },
      })
    }

    return {
      applicationId: this.#store.selectSignal(selectAppId)(),
      processResourceId: this.#store.selectSignal(selectProcessResourceId)(),
      message: payload.message ?? '',
      documents: [],
      contextData: {
        items: [
          ...payload.context.items,
          ...(payload.userPromptRequired
            ? [
                {
                  key: ContextDataKeys.UiAgent,
                  itemType: ConversationThreadContextDataItemType.GenericString,
                  valueGenericString: JSON.stringify(payload.agent),
                },
              ]
            : []),
          {
            key: ContextDataKeys.UiTarget,
            itemType: ConversationThreadContextDataItemType.GenericString,
            valueGenericString: JSON.stringify(payload.target),
          },
          {
            key: ContextDataKeys.UIMessageType,
            itemType: ConversationThreadContextDataItemType.GenericString,
            valueGenericString: payload.userPromptRequired
              ? MessageType.SystemMsg
              : MessageType.UserMsg,
          },
        ],
      },
      hint: {
        autoStart: false,
        actions: [
          {
            actionType: ConversationThreadExecutionPlanActionType.Automatic,
            agent: finalAgent,
            localId: uuidv4(),
            context: { items: actionContextItems },
            description: '',
            name: '',
            parallel: false,
            target: payload.target ?? {
              name: null,
              targetType: null,
            },
          },
        ],
      },
    }
  }

  buildMessageTypeContextItem(messageType: string): ContextItem {
    return {
      key: ContextDataKeys.UIMessageType,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: messageType,
    }
  }

  buildIntentUnderstandingHint(file: {
    id: string
    type: string
    name: string
  }): ConversationMessageHint {
    // Build hint with file context in the action
    // For intent understanding (connected graph), we always use DesignerReferenceImage
    // The actual agent will be determined later
    return {
      autoStart: false,
      actions: [
        {
          actionType: ConversationThreadExecutionPlanActionType.Automatic,
          agent: {
            action: AgentAction.NoopStore,
            agent: AgentName.ConnectedGraph,
          },
          localId: uuidv4(),
          context: {
            items: [
              {
                key: ContextDataKeys.DesignerReferenceImage,
                itemType: ConversationThreadContextDataItemType.BinaryObject,
                valueBinaryObject: {
                  tempFileId: file.id,
                  fileName: file.name,
                  mimeType: file.type,
                },
              },
            ],
          },
          description: '',
          name: '',
          parallel: false,
          target: {
            name: null,
            targetType: null,
          },
        },
      ],
    }
  }

  deduplicateContextItems(items: ContextItem[]): ContextItem[] {
    return _uniqBy('key', [...items].reverse()).reverse()
  }

  /**
   * Async PROJECT_DATA_MODEL context item for create_ui generation paths. Resolves to the catalogue
   * item, or `null` when the project/library catalogue is empty (so callers can skip pushing it).
   * The catalogue fetch is best-effort and never throws — see #buildProjectDataModelCatalogue.
   */
  buildProjectDataModelContextItem(): Observable<ContextItem | null> {
    return this.#buildProjectDataModelCatalogue().pipe(
      map((catalogue) => this.#mapCatalogueToContextItem(catalogue))
    )
  }

  #buildIntentUnderstandingMessageBaseContext(
    templateConfig: NavigationAreaNormalizedTemplateConfigs = null
  ): ContextData {
    const processDefinition = ProcessEffector.processDefinition$.getState()
    const allNodes = this.#store.selectSignal(selectNodes)()
    const contextItems: Record<string, any>[] = []
    const { appVersionId } = this.#urlParamsStore.urlParameters

    if (processDefinition) {
      contextItems.push({
        key: ContextDataKeys.ProcessDefinition,
        itemType: ConversationThreadContextDataItemType.GenericString,
        value: {
          id: processDefinition.id,
          resourceName: processDefinition.resourceName,
          description: processDefinition.description,
          processDefinitionId: processDefinition.id,
          resourceId: processDefinition.resourceId,
        },
      })
    }

    if (allNodes && allNodes.length > 0) {
      contextItems.push({
        key: ContextDataKeys.ProcessNodes,
        itemType: ConversationThreadContextDataItemType.GenericString,
        value: allNodes.map((node) => ({
          id: node.id,
          flowxUuid: node.flowxUuid,
          name: node.name,
          type: node.type,
          actionDefinitions:
            node.actionDefinitions?.map((action) => ({
              id: action.id,
              flowxUuid: action.flowxUuid,
              handlerService: action.handlerService,
              name: action.name,
            })) || [],
        })),
      })
    }

    if (templateConfig) {
      const trimmedTemplateConfig = {}

      allNodes.forEach((node) => {
        const trimmedNodeConfig = { entities: { templateConfigs: {} } }

        if (!templateConfig[node.navigationAreaId]) {
          return
        }

        Object.values(templateConfig[node.navigationAreaId]).forEach((config: any) => {
          trimmedNodeConfig.entities.templateConfigs[config.id] =
            mapTemplateConfigToAiContext(config)
        })

        trimmedTemplateConfig[node.navigationAreaId] = trimmedNodeConfig
      })

      contextItems.push({
        key: ContextDataKeys.UiTemplateDefinition,
        itemType: ConversationThreadContextDataItemType.GenericString,
        value: trimmedTemplateConfig,
      })
    }

    return {
      items: [
        {
          key: ContextDataKeys.Process,
          itemType: ConversationThreadContextDataItemType.GenericString,
          valueGenericString: JSON.stringify(contextItems),
        },
        {
          key: ContextDataKeys.ProcessReference,
          itemType: ConversationThreadContextDataItemType.ProcessReference,
          valueProcessReference: {
            id: processDefinition.resourceId,
            applicationId: appVersionId,
            versionId: '',
          },
        },
      ],
    }
  }

  #buildStandardMessageInput(
    message: ChatMessage & { file?: { id: string; type: string; name: string } },
    agent: ActionAgent,
    latestMessage: Message | null,
    documents?: ConversationDocumentReferenceInput[]
  ): AddThreadMessageRequest {
    const previousContextItems =
      latestMessage?.contextData?.items
        ?.filter(
          (item: ContextItem) =>
            item.key !== ContextDataKeys.UiAgent && item.key !== ContextDataKeys.UIMessageType
        )
        .map(_omit(['__typename'])) ?? []

    return {
      message: message.prompt,
      documents: documents ?? [],
      contextData: {
        items: [
          {
            key: ContextDataKeys.UIMessageType,
            itemType: ConversationThreadContextDataItemType.GenericString,
            valueGenericString: message.type,
          },
          ...previousContextItems,
        ] as ContextItem[],
      },
      hint: {
        autoStart: false,
        agent: agent.agent,
        actions: [],
      },
    }
  }

  #buildFirstUserPromptGraphMessageInput(
    message: ChatMessage & { file?: { id: string; type: string; name: string } },
    latestMessage: Message,
    documents?: ConversationDocumentReferenceInput[]
  ): AddThreadMessageRequest {
    const uiAgentItem = latestMessage.contextData.items.find(
      (item: ContextItem) => item.key === ContextDataKeys.UiAgent
    )

    if (!uiAgentItem?.valueGenericString) {
      throw new Error('UI_AGENT context not found in latest message')
    }

    const lastMessageAgentInfo = JSON.parse(uiAgentItem.valueGenericString) as ActionAgent

    return {
      message: message.prompt,
      documents: documents ?? [],
      contextData: {
        items: [
          {
            key: ContextDataKeys.UIMessageType,
            itemType: ConversationThreadContextDataItemType.GenericString,
            valueGenericString: message.type,
          },
          ...latestMessage.contextData.items
            .filter(
              (item: ContextItem) =>
                item.key !== ContextDataKeys.UiAgent && item.key !== ContextDataKeys.UIMessageType
            )
            .map(_omit(['__typename'])),
        ] as ContextItem[],
      },
      hint: {
        autoStart: false,
        actions: [
          {
            actionType: ConversationThreadExecutionPlanActionType.Automatic,
            agent: lastMessageAgentInfo,
            localId: uuidv4(),
            context: { items: [] },
            parallel: false,
            target: {
              name: null,
              targetType: null,
            },
            description: '',
            name: '',
          },
        ],
      },
    }
  }

  #isFollowUpAfterInitMessage(threadData: {
    activeThread: ConversationThread
    latestMessage: Message | null
    firstMessage: Message
  }): boolean {
    const { activeThread, latestMessage } = threadData
    return (
      activeThread.messages.length === 1 &&
      latestMessage?.executionPlan.actions[0]?.agent.agent === AgentName.ConnectedGraph &&
      latestMessage?.executionPlan.actions[0]?.agent.action === AgentAction.NoopStore
    )
  }

  #mapCatalogueToContextItem(catalogue: ProjectDataModelCatalogueEntry[]): ContextItem | null {
    if (!catalogue.length) {
      return null
    }

    return {
      key: ContextDataKeys.ProjectDataModel,
      itemType: ConversationThreadContextDataItemType.GenericString,
      valueGenericString: JSON.stringify(catalogue),
    }
  }

  #buildProjectDataModelCatalogue(): Observable<ProjectDataModelCatalogueEntry[]> {
    const { appId, appVersionId } = this.#urlParamsStore.urlParameters

    return this.#projectDataModelApiService.getProjectDataModel().pipe(
      map((resp) => {
        const manifestMap = (resp.manifestInfo ?? []).reduce<Record<string, ManifestInfo>>(
          (acc, manifest) => {
            acc[manifest.resId] = manifest
            return acc
          },
          {}
        )

        return mapProjectDataModelToCatalogue(resp.resourceContent.content, manifestMap, {
          appId,
          appVersionId,
        })
      }),
      // The catalogue is best-effort context; never block message sending on its failure.
      catchError(() => of([] as ProjectDataModelCatalogueEntry[]))
    )
  }
}
